
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Package, Loader2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const vippsOrderId = searchParams.get('vipps_order_id');
  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending' | 'failed' | null>(null);
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    const verifyPayment = async () => {
      const paymentId = sessionId || vippsOrderId;
      
      if (!paymentId) {
        setIsVerifying(false);
        setPaymentStatus('failed');
        return;
      }

      try {
        console.log('Verifying payment for ID:', paymentId);
        
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { session_id: paymentId }
        });

        if (error) {
          console.error('Error verifying payment:', error);
          throw error;
        }

        console.log('Payment verification response:', data);

        if (data.status === 'paid') {
          setPaymentStatus('paid');
          setSessionDetails(data.session);
          toast({
            title: "Betaling bekreftet!",
            description: vippsOrderId ? 
              "Din Vipps-betaling er registrert og betalt." : 
              "Din bestilling er registrert og betalt.",
          });
        } else {
          setPaymentStatus('pending');
          toast({
            title: "Betaling under behandling",
            description: "Vi behandler fortsatt betalingen din.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('Payment verification failed:', error);
        setPaymentStatus('failed');
        toast({
          title: "Kunne ikke verifisere betaling",
          description: "Det oppstod en feil ved verifikasjon av betalingen.",
          variant: "destructive",
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId, vippsOrderId, toast]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-brand-deepSpace">
        <div className="star-field"></div>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-brand-deepSpace/70 backdrop-blur-sm border border-brand-electricBlue/30">
              <CardContent className="py-16 text-center">
                <Loader2 className="w-12 h-12 text-brand-neonGreen mx-auto mb-4 animate-spin" />
                <h2 className="text-2xl font-bold text-white mb-2">
                  Verifiserer betaling...
                </h2>
                <p className="text-gray-300">
                  Vi sjekker statusen på betalingen din.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (paymentStatus === 'failed' || (!sessionId && !vippsOrderId)) {
    return (
      <div className="min-h-screen bg-brand-deepSpace">
        <div className="star-field"></div>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-brand-deepSpace/70 backdrop-blur-sm border border-red-500/30">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <CardTitle className="text-2xl font-bold text-white">
                  Feil ved betaling
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-center">
                <p className="text-lg text-gray-300">
                  Vi kunne ikke verifisere betalingen din. Kontakt oss hvis du mener dette er en feil.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild>
                    <Link to="/">
                      Tilbake til forsiden
                    </Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.href = "mailto:kundeservice@peveo.no"}
                  >
                    Kontakt support
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-deepSpace">
      <div className="star-field"></div>
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-brand-deepSpace/70 backdrop-blur-sm border border-brand-neonGreen/30">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-brand-neonGreen/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-brand-neonGreen" />
              </div>
              <CardTitle className="text-2xl font-bold text-white">
                Betaling vellykket! 🎉
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 text-center">
              <p className="text-lg text-gray-300">
                Takk for ditt kjøp! Betalingen er bekreftet og registrert.
                {vippsOrderId && " Betalt via Vipps."}
              </p>
              
              {sessionDetails && (
                <div className="bg-brand-deepSpace/50 p-4 rounded-lg border border-brand-electricBlue/30">
                  <p className="text-sm text-gray-400">Ordredetaljer:</p>
                  <p className="font-mono text-brand-electricBlue">{sessionDetails.id}</p>
                  {sessionDetails.customer_email && (
                    <p className="text-sm text-gray-300 mt-2">{sessionDetails.customer_email}</p>
                  )}
                </div>
              )}

              <div className="bg-brand-deepSpace/50 p-6 rounded-lg border border-brand-electricBlue/30">
                <Package className="w-8 h-8 text-brand-neonGreen mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Hva skjer nå?</h3>
                <ul className="text-left space-y-2 text-gray-300">
                  <li>• Du får en e-postbekreftelse med alle detaljer</li>
                  <li>• Vi behandler og sender bestillingen din</li>
                  <li>• Sporingsinformasjon sendes når pakken er på vei</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild>
                  <Link to="/">
                    Tilbake til forsiden
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/products/pre-workout">
                    Se produktet igjen
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
